import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Activity, Compass, Users, BookOpen, Handshake, Calendar, Heart,
  FileText, MessageSquare, Settings, BookmarkCheck, Megaphone, Plus, Trash2,
  X, MapPin, DollarSign, Clock, Video, User, Handshake as HandshakeIcon, Loader2,
} from 'lucide-react';
import { DashboardShell } from '../components/brand/DashboardShell';
import { NegotiateModal } from '../components/brand/NegotiateModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABELS = {
  in_home_care: 'In-home care', telehealth_visit: 'Telehealth', wellness_visit: 'Wellness',
  companion_care: 'Companion care', post_discharge: 'Post-discharge', chronic_care: 'Chronic care',
  hospice_palliative: 'Hospice & palliative', mobile_urgent_care: 'Mobile urgent', behavioral_health: 'Behavioral health',
  skilled_nursing: 'Skilled nursing',
};
const URGENCY_LABELS = { routine: 'Routine', soon: 'Soon', same_day: 'Same day', urgent: 'Urgent' };
const states = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

export default function CareRequests() {
  const { getAuthHeader, token } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPost, setShowPost] = useState(false);
  const [negotiateTarget, setNegotiateTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/care-requests/mine`, getAuthHeader());
      setRequests(data.care_requests || []);
    } catch (e) {
      toast.error('Could not load your care requests');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const closeRequest = async (id) => {
    try {
      await axios.post(`${API}/care-requests/${id}/close`, {}, getAuthHeader());
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status: 'closed' } : r)));
      toast.success('Request closed');
    } catch (e) { toast.error('Could not close'); }
  };

  const deleteRequest = async (id) => {
    try {
      await axios.delete(`${API}/care-requests/${id}`, getAuthHeader());
      setRequests((prev) => prev.filter((r) => r.id !== id));
      toast.success('Request deleted');
    } catch (e) { toast.error('Could not delete'); }
  };

  const navItems = [
    { icon: Activity, label: 'Dashboard', href: '/patient/dashboard' },
    { icon: Compass, label: 'Care Navigator', href: '/patient/navigator', highlight: true },
    { icon: Users, label: 'Find Clinicians', href: '/patient/find-clinicians' },
    { icon: BookmarkCheck, label: 'Saved Searches', href: '/patient/saved-searches' },
    { icon: Megaphone, label: 'Post a Need', active: true },
    { icon: BookOpen, label: 'Browse Marketplace', href: '/marketplace' },
    { icon: Handshake, label: 'Offers & Bookings', href: '/patient/offers' },
    { icon: Calendar, label: 'Appointments', href: '/patient/dashboard#appointments' },
    { icon: Heart, label: 'Daily Check-in', href: '/patient/checkin' },
    { icon: Users, label: 'Family Portal', href: '/patient/family' },
    { icon: FileText, label: 'Documents', href: '/patient/documents' },
    { icon: MessageSquare, label: 'Messages', href: '/patient/messages' },
    { icon: Settings, label: 'Settings', href: '#' },
  ];

  return (
    <DashboardShell
      navItems={navItems}
      title="Post a need, let clinicians come to you"
      subtitle="Describe the care you're looking for. Verified clinicians who fit will reach out with their interest and rate — no searching required."
      rightSlot={
        <Button onClick={() => setShowPost(true)} className="cn-btn cn-btn-primary" data-testid="post-need-btn">
          <Plus className="w-4 h-4" /> Post a need
        </Button>
      }
      testId="care-requests-page"
    >
      {loading ? (
        <div className="grid sm:grid-cols-2 gap-4">{[1, 2].map((i) => <div key={i} className="cn-card p-6 h-40 animate-pulse bg-cn-bg/60" />)}</div>
      ) : requests.length === 0 ? (
        <div className="cn-card p-12 text-center bg-gradient-to-br from-white to-cn-bg/60" data-testid="care-requests-empty">
          <div className="w-20 h-20 rounded-3xl bg-cn-accent/10 text-cn-accent mx-auto flex items-center justify-center mb-4">
            <Megaphone className="w-10 h-10" />
          </div>
          <h3 className="cn-display text-2xl text-cn-text dark:text-white">No care requests yet</h3>
          <p className="text-sm text-cn-text-muted mt-2 max-w-md mx-auto">
            Flip the marketplace around — post what you need and let the right clinicians come to you.
          </p>
          <Button onClick={() => setShowPost(true)} className="cn-btn cn-btn-primary mt-5" data-testid="empty-post-btn">
            <Plus className="w-4 h-4" /> Post your first need
          </Button>
        </div>
      ) : (
        <div className="space-y-5">
          {requests.map((r) => (
            <div key={r.id} className="cn-card bg-white dark:bg-[#0F172A] border border-cn-border/70 p-6" data-testid={`care-request-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="cn-display text-lg text-cn-text dark:text-white">{r.title}</h3>
                    <Badge className={r.status === 'open' ? 'bg-cn-success/10 text-cn-success border border-cn-success/20' : 'bg-cn-text-muted/10 text-cn-text-muted'}>
                      {r.status}
                    </Badge>
                  </div>
                  {r.description && <p className="text-sm text-cn-text-muted mt-1">{r.description}</p>}
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    {r.service_type && <Badge variant="outline" className="border-cn-border">{SERVICE_LABELS[r.service_type] || r.service_type}</Badge>}
                    {r.state && <Badge variant="outline" className="border-cn-border"><MapPin className="w-3 h-3 mr-1" />{r.state}</Badge>}
                    {r.visit_type && <Badge variant="outline" className="border-cn-border">{r.visit_type === 'telehealth' ? <Video className="w-3 h-3 mr-1" /> : <User className="w-3 h-3 mr-1" />}{r.visit_type.replace('_', ' ')}</Badge>}
                    {r.urgency && <Badge variant="outline" className="border-cn-border"><Clock className="w-3 h-3 mr-1" />{URGENCY_LABELS[r.urgency] || r.urgency}</Badge>}
                    {r.budget_max && <Badge variant="outline" className="border-cn-border"><DollarSign className="w-3 h-3 mr-1" />Up to ${r.budget_max}/hr</Badge>}
                  </div>
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  {r.status === 'open' && (
                    <Button onClick={() => closeRequest(r.id)} className="cn-btn cn-btn-ghost !py-1.5 !px-3 text-xs" data-testid={`close-request-${r.id}`}>
                      <X className="w-3.5 h-3.5" /> Close
                    </Button>
                  )}
                  <Button onClick={() => deleteRequest(r.id)} className="cn-btn cn-btn-ghost !py-1.5 !px-3 text-xs text-cn-error" data-testid={`delete-request-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </Button>
                </div>
              </div>

              {/* Responses */}
              <div className="mt-5 pt-4 border-t border-cn-border/60">
                <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-3">
                  {r.response_count} clinician response{r.response_count !== 1 ? 's' : ''}
                </p>
                {(!r.responses || r.responses.length === 0) ? (
                  <p className="text-sm text-cn-text-muted italic">No responses yet. We'll notify you the moment a clinician reaches out.</p>
                ) : (
                  <div className="space-y-3">
                    {r.responses.map((resp) => (
                      <div key={resp.id} className="p-4 rounded-2xl bg-cn-bg/60 border border-cn-border/50" data-testid={`response-${resp.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-cn-primary to-cn-secondary text-white flex items-center justify-center font-bold">
                              {resp.clinician_name?.charAt(0) || 'C'}
                            </div>
                            <div>
                              <p className="font-display font-bold text-cn-text dark:text-white">{resp.clinician_name}</p>
                              <Badge variant="outline" className="border-cn-border text-xs">{resp.license_type}</Badge>
                            </div>
                          </div>
                          {resp.proposed_rate != null && (
                            <div className="text-right">
                              <p className="font-display font-extrabold text-xl text-cn-primary leading-none">${resp.proposed_rate}</p>
                              <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">per hour</p>
                            </div>
                          )}
                        </div>
                        {resp.message && <p className="mt-3 text-sm text-cn-text italic border-l-2 border-cn-primary/40 pl-3">"{resp.message}"</p>}
                        <div className="mt-3 flex gap-2">
                          <Button
                            onClick={() => setNegotiateTarget({ id: resp.clinician_id, name: resp.clinician_name })}
                            className="cn-btn cn-btn-primary !py-2 text-sm"
                            data-testid={`negotiate-response-${resp.id}`}
                          >
                            <HandshakeIcon className="w-4 h-4" /> Negotiate & Book
                          </Button>
                          <Button onClick={() => navigate('/patient/messages')} className="cn-btn cn-btn-ghost !py-2 text-sm" data-testid={`message-response-${resp.id}`}>
                            <MessageSquare className="w-4 h-4" /> Message
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <PostNeedModal open={showPost} onOpenChange={setShowPost} getAuthHeader={getAuthHeader} onCreated={(r) => { setRequests((prev) => [r, ...prev]); }} />

      <NegotiateModal
        open={!!negotiateTarget}
        onOpenChange={(v) => !v && setNegotiateTarget(null)}
        clinician={negotiateTarget}
        token={token}
        onCreated={() => navigate('/patient/offers')}
      />
    </DashboardShell>
  );
}

const PostNeedModal = ({ open, onOpenChange, getAuthHeader, onCreated }) => {
  const [form, setForm] = useState({ title: '', description: '', service_type: 'in_home_care', state: '', visit_type: 'in_person', urgency: 'soon', budget_max: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm({ title: '', description: '', service_type: 'in_home_care', state: '', visit_type: 'in_person', urgency: 'soon', budget_max: '' });
  }, [open]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Give your request a short title'); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, budget_max: form.budget_max ? Number(form.budget_max) : null };
      const { data } = await axios.post(`${API}/care-requests`, payload, getAuthHeader());
      toast.success('Your need is live — clinicians can now reach out');
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not post your need');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="post-need-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-cn-text">Post a care need</DialogTitle>
          <DialogDescription className="text-cn-text-muted">Tell verified clinicians what you're looking for. The right ones will come to you.</DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          <div>
            <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Weekday RN for mom with dementia" className="mt-1.5 h-11 rounded-xl" data-testid="need-title" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Details</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Schedule, must-haves, preferences, anything a clinician should know…" className="mt-1.5 rounded-xl" rows={3} data-testid="need-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Care type</Label>
              <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="need-service"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(SERVICE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">State</Label>
              <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="need-state"><SelectValue placeholder="State" /></SelectTrigger>
                <SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Visit</Label>
              <Select value={form.visit_type} onValueChange={(v) => setForm({ ...form, visit_type: v })}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="need-visit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_person">In person</SelectItem>
                  <SelectItem value="telehealth">Telehealth</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Urgency</Label>
              <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="need-urgency"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(URGENCY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Budget $/hr</Label>
              <Input type="number" min={0} value={form.budget_max} onChange={(e) => setForm({ ...form, budget_max: e.target.value })} placeholder="80" className="mt-1.5 h-11 rounded-xl" data-testid="need-budget" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={() => onOpenChange(false)} className="cn-btn cn-btn-ghost flex-1" data-testid="need-cancel">Cancel</Button>
            <Button onClick={submit} disabled={submitting} className="cn-btn cn-btn-primary flex-1" data-testid="need-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Megaphone className="w-4 h-4" /> Post need</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
